import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { routes, routes2 } from '@linqpal/models'
import { ErrorCode } from '@linqpal/models/src/types/exceptions'
import fb from '../../../utils/service/fb'
import cooker from '../../../utils/service/cooker'
import SignIn from './SignIn'
import SignInPassword from './SignInPassword'
import SignInVerify from './SignInVerify'
import type { onNextPayload as SingInLoginNextPayload } from './SignIn'
import type { onNextPayload as SignInPasswordPayload } from './SignInPassword'
import type { onNextPayload as SignInPhoneVerifyPayload } from './SignInVerify'
import RootStore from '../../../store'
import { useNavigation } from '@react-navigation/native'
import { paths } from '../../links'
import { currentZDate } from '@linqpal/models/src/helpers/date'
import { PAGE_MODE } from '../InvoiceDetails/utils/pageMode'
import { EVENT_TYPE } from '../InvoiceDetails/utils/eventTypes'
import { SIGN_IN_STEPS, State } from './types'
import moment from 'moment'

interface SingInFlowProps {
  onSuccess: () => void
  onSignUp?: () => void
  initialStep?: SIGN_IN_STEPS
  initialState?: State
  mode?: PAGE_MODE
}

export const SignInFlow: React.FC<SingInFlowProps> = memo(
  ({
    onSuccess,
    onSignUp,
    mode,
    initialStep = SIGN_IN_STEPS.SIGN_IN_LOGIN,
    initialState = {},
  }) => {
    const navigation = useNavigation() as any
    const { userStore } = RootStore

    const [step, setStep] = useState(initialStep)
    const [state, setState] = useState<State>(initialState)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<ErrorCode | false>(false)
    const expectedOrigin = window.location.origin
    let windowFeatures
    if (mode && mode === PAGE_MODE.POPUP)
      windowFeatures = 'width=800,height=600,scrollbars=yes,resizable=yes'

    const confirmationResultRef = useRef<fb.auth.ConfirmationResult | null>(
      null,
    )

    const [popupWindow, setPopupWindow] = useState<Window | null>(null)
    const [forgotPassPopupWindow, setForgotPassPopupWindow] =
      useState<Window | null>(null)

    const handleNext = useCallback(
      async (data: any) => {
        setError(false)

        if (step === SIGN_IN_STEPS.SIGN_IN_LOGIN) {
          const { login, type, verifier } = data as SingInLoginNextPayload

          try {
            setLoading(true)
            const res = await routes2.user.check({
              login: login.toLowerCase(),
            })

            setState({ ...state, ...data, verifier, isNewUser: !res.exists })
          } catch (err) {
            console.error(err)
          }

          if (type === 'email') {
            setStep(SIGN_IN_STEPS.SIGN_IN_PASSWORD)
          }

          if (type === 'phone') {
            const phone = login
            try {
              confirmationResultRef.current = await fb
                .auth()
                .signInWithPhoneNumber(phone, verifier)
              setStep(SIGN_IN_STEPS.SIGN_IN_PHONE_VERIFY)
            } catch (err) {
              console.error(err)
              const typedError = err as Error & { code?: string }
              const { code } = typedError
              if (code) {
                if (code === 'auth/invalid-phone-number')
                  setError('auth/wrong-password')
              }
            }
          }

          setLoading(false)
        }

        if (step === SIGN_IN_STEPS.SIGN_IN_PASSWORD) {
          const { login: email } = state

          try {
            setLoading(true)
            const { password } = data as SignInPasswordPayload

            const fbAuthResult = await fb
              .auth()
              .signInWithEmailAndPassword(email as string, password)
            const idToken = await fbAuthResult.user?.getIdToken()
            if (!idToken)
              throw new Error('Unable to receive a token from the firebase')
            const blueTapeAuthResult = await routes2.user.webAuth({ idToken })

            const { session, challenge } = blueTapeAuthResult
            cooker.set('session', session, { maxAge: 60 * 60 })
            cooker.set('auth-challenge', challenge, {
              maxAge: 80 * 24 * 60 * 60,
            })
            localStorage.setItem('lastInteractionTime', moment().toString())

            await userStore.setAuthState(session)

            await routes.user.conversion({
              lastLogin: currentZDate(),
            })

            onSuccess()
          } catch (err) {
            console.error(err)
            const typedError = err as Error & { code?: string }
            const { code } = typedError

            if (code) {
              if (code === 'auth/wrong-password')
                setError('auth/wrong-password')
              if (code === 'auth/user-not-found')
                setError('auth/user-not-found')
              if (code === 'auth/too-many-requests')
                setError('auth/too-many-requests')
            } else {
              setError('auth/user-not-found')
            }
          }

          setLoading(false)
        }

        if (step === SIGN_IN_STEPS.SIGN_IN_PHONE_VERIFY) {
          const { otp } = data as SignInPhoneVerifyPayload
          const confirmationResult = confirmationResultRef.current
          if (!confirmationResult) return

          try {
            setLoading(true)
            const fbAuthResult = await confirmationResult.confirm(otp)
            const idToken = await fbAuthResult.user?.getIdToken()
            if (!idToken)
              throw new Error('Unable to receive a token from the firebase')
            const blueTapeAuthResult = await routes2.user.webAuth({ idToken })
            const { session, challenge } = blueTapeAuthResult
            cooker.set('session', session, { maxAge: 60 * 60 })
            cooker.set('auth-challenge', challenge, {
              maxAge: 80 * 24 * 60 * 60,
            })

            localStorage.setItem('lastInteractionTime', moment().toString())
            userStore.setIsAuthenticated(true)
            onSuccess()
          } catch (err) {
            console.error(err)
            const typedError = err as Error & { code?: string }
            const { code } = typedError
            if (code) {
              if (code === 'auth/invalid-verification-code')
                setError('auth/invalid-verification-code')
            } else {
              setError('auth/user-not-found')
            }
          }
          setLoading(false)
        }
      },
      [onSuccess, state, step, userStore],
    )

    const handleBack = useCallback(() => {
      setError(false)

      if (
        step === SIGN_IN_STEPS.SIGN_IN_PHONE_VERIFY ||
        step === SIGN_IN_STEPS.SIGN_IN_PASSWORD
      ) {
        setStep(SIGN_IN_STEPS.SIGN_IN_LOGIN)
      }
    }, [step])

    const handleResendSmsCode = useCallback(async () => {
      const { login: phone, verifier } = state
      try {
        if (!phone) throw new Error('no phone')
        if (!verifier) throw new Error('no verifyer')
        confirmationResultRef.current = await fb
          .auth()
          .signInWithPhoneNumber(phone, verifier)
      } catch (err) {
        console.error(err)
      }
    }, [state])

    const handleSignUp = useCallback(async () => {
      if (onSignUp) onSignUp()
      else {
        if (mode) {
          const newWindow = window.open('/signup', '_blank', windowFeatures)
          setPopupWindow(newWindow)
        } else {
          navigation.navigate(paths.Auth._self, {
            screen: paths.Auth.SignUpHome,
          })
        }
      }
    }, [windowFeatures, navigation, mode, onSignUp])

    const handleForgotPassword = useCallback(() => {
      const newWindow = window.open(
        `/forgot-password`,
        '_blank',
        windowFeatures,
      )
      setForgotPassPopupWindow(newWindow)
    }, [windowFeatures])

    useEffect(() => {
      const handleMessage = (event: any) => {
        if (event.origin === expectedOrigin) {
          console.log('here event.data', event.data.type)
          if (event.data.type === EVENT_TYPE.USER_SIGNED_UP && popupWindow) {
            popupWindow.close()
            setPopupWindow(null)
            userStore.setIsAuthenticated(true)
            onSuccess()
          } else if (
            event.data.type === EVENT_TYPE.PASS_RESTORE_INSTRUCTIONS_SENT &&
            forgotPassPopupWindow
          ) {
            forgotPassPopupWindow.close()
            setForgotPassPopupWindow(null)
            setStep(SIGN_IN_STEPS.SIGN_IN_PASSWORD)
          }
        } else {
          console.warn('Received message from unexpected origin:', event.origin)
        }
      }
      window.addEventListener('message', handleMessage)
      return () => {
        window.removeEventListener('message', handleMessage)
      }
    }, [
      popupWindow,
      onSuccess,
      userStore,
      expectedOrigin,
      forgotPassPopupWindow,
    ])

    return (
      <>
        {step === SIGN_IN_STEPS.SIGN_IN_LOGIN && (
          <SignIn
            onNext={handleNext}
            onSignUp={handleSignUp}
            login={state.login}
            loading={loading}
            error={error}
          />
        )}
        {step === SIGN_IN_STEPS.SIGN_IN_PASSWORD && (
          <SignInPassword
            onNext={handleNext}
            onBack={handleBack}
            onForgotPassword={handleForgotPassword}
            onSignUp={handleSignUp}
            login={state.login as string}
            loading={loading}
            error={error}
          />
        )}
        {step === SIGN_IN_STEPS.SIGN_IN_PHONE_VERIFY && (
          <SignInVerify
            onNext={handleNext}
            onBack={handleBack}
            onSignUp={handleSignUp}
            onResendSmsCode={handleResendSmsCode}
            login={state.login as string}
            loading={loading}
          />
        )}
      </>
    )
  },
)
